// Intended to be an attribute component
// Therefore it will follow the directive selector syntax
/* tslint:disable:component-selector */

import {
  Component,
  Input,
  HostBinding,
  ViewEncapsulation,
  SimpleChanges,
  OnChanges,
  ElementRef,
  Renderer2,
  OnInit,
  HostListener,
} from '@angular/core';
import { ButtonStyles } from './button-styles';

export type PgButtonType =
  | 'primary'
  | 'secondary'
  | 'subtle'
  | 'destroy'
  | 'success'
  | 'primary-light'
  | 'secondary-light'
  | 'subtle-light'
  | 'destroy-light'
  | 'success-light';

@Component({
  selector: 'button[pgButton], a[pgButton], button[gritButton], a[gritButton]',
  exportAs: 'pgButton, gritButton',
  templateUrl: './pg-button.component.html',
  styleUrls: ['./pg-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PgButtonComponent implements OnChanges, OnInit {
  /**
   * @deprecated Since version 1.0.0 Will be deleted in version 2.0.0 Use color instead.
   */
  @Input() type: PgButtonType;

  @Input() color: PgButtonType;

  @Input() disabled: boolean;

  @Input() loading: boolean;

  @HostBinding('class.pg-button-scale-down-on-click')
  @Input()
  scaleDownOnClick = true;

  @Input() size: 'sm' | 'md' | 'lg' | 'responsive' | null;

  isHovered = false;
  isPressed = false;

  @HostBinding('class.pg-button') _classPgButton = true;
  @HostBinding('class.is-disabled')
  get _classIsDisabled() {
    return this.disabled;
  }

  @HostBinding('class.is-loading')
  get _classIsLoading() {
    return this.loading;
  }

  @HostBinding('class.sm')
  get _classSm() {
    return this.size === 'sm';
  }

  @HostBinding('class.md')
  get _classMd() {
    return this.size === 'md';
  }

  @HostBinding('class.lg')
  get _classLg() {
    return this.size === 'lg';
  }

  @HostBinding('class.responsive')
  get _classResponsive() {
    return this.size === 'responsive';
  }

  @HostBinding('style.background')
  get _styleBackground() {
    if (this.isPressed) {
      return this.styleOverrides.backgroundColorPressed;
    } else if (this.isHovered) {
      return this.styleOverrides.backgroundColorHover;
    }
    return this.styleOverrides.backgroundColorIdle;
  }
  @HostBinding('style.color')
  get _styleColor() {
    if (this.isPressed) {
      return this.styleOverrides.foregroundColorPressed;
    } else if (this.isHovered) {
      return this.styleOverrides.foregroundColorHover;
    }
    return this.styleOverrides.foregroundColorIdle;
  }

  @HostBinding('style.boxShadow')
  get _styleShadow() {
    return this.styleOverrides.boxShadow;
  }

  @HostBinding('style.fontWeight')
  get _styleFontWeight() {
    return this.styleOverrides.fontWeight;
  }

  @HostBinding('style.borderRadius.px')
  get _styleBorderRadius() {
    return this.styleOverrides.borderRadius;
  }

  @HostBinding('style.fontFamily')
  get _styleFontFamily() {
    return this.styleOverrides.fontFamily;
  }

  @HostBinding('style.min-height.px')
  @HostBinding('style.max-height.px')
  get _styleHeight() {
    return this.styleOverrides.height;
  }

  @Input() styleOverrides: ButtonStyles = {};

  constructor(public elementRef: ElementRef, public renderer: Renderer2) {}

  ngOnInit() {}
  ngOnChanges(changes: SimpleChanges) {
    // TODO: Deprecated for color
    if (changes.type) {
      this.renderer.removeClass(
        this.elementRef.nativeElement,
        changes.type.previousValue
      );
      this.renderer.addClass(
        this.elementRef.nativeElement,
        changes.type.currentValue
      );
    }

    if (changes.color) {
      this.renderer.removeClass(
        this.elementRef.nativeElement,
        changes.color.previousValue
      );
      this.renderer.addClass(
        this.elementRef.nativeElement,
        changes.color.currentValue
      );
    }
  }

  @HostListener('mouseenter', ['$event'])
  public onMouseEnter(event: any) {
    this.isHovered = true;
  }

  @HostListener('mouseout', ['$event'])
  public onMouseExit(event: any) {
    this.isHovered = false;
  }

  @HostListener('touchstart', ['$event'])
  @HostListener('mousedown', ['$event'])
  public onMouseDown(event: any) {
    this.isPressed = true;
  }

  @HostListener('touchend', ['$event'])
  @HostListener('window:mouseup', ['$event'])
  public onWindowMouseUp(event: any) {
    this.isPressed = false;
  }

  @HostListener('click', ['$event'])
  @HostListener('keyup', ['$event'])
  @HostListener('keydown', ['$event'])
  @HostListener('keypress', ['$event'])
  public _haltDisabledEvents(event: Event) {
    // A disabled button shouldn't apply any actions
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
